import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { 
  getRecordGroups, 
  getRecordGroup,
  getRecordGroupSummary,
  searchRecordGroups,
  createRecordGroup, 
  updateRecordGroup, 
  deleteRecordGroup,
  setDefaultRecordTag,
  getTrashedRecordGroups,
  getRecordGroupTrashStats,
  restoreRecordGroup,
  permanentlyDeleteRecordGroup,
  triggerRecordGroupTrashCleanup,
} from '@/services/api/record-group.service'
import type { CreateRecordGroupRequest, RecordGroupSearchParams } from '@/types/api/record-group'

import { useWorkspace } from './useWorkspace'

/** Create/update/delete and default-tag mutations; every record group query is refreshed afterwards. */
export function useRecordGroupMutations() {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'record-groups'] })

  const createMutation = useMutation({ mutationFn: createRecordGroup, onSuccess: invalidate })
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateRecordGroupRequest }) => updateRecordGroup(id, data),
    onSuccess: invalidate,
  })
  const deleteMutation = useMutation({ mutationFn: deleteRecordGroup, onSuccess: invalidate })
  const defaultTagMutation = useMutation({
    mutationFn: ({ id, tagId }: { id: string; tagId: string | null }) => setDefaultRecordTag(id, tagId),
    onSuccess: invalidate,
  })

  return {
    setDefaultRecordTag: defaultTagMutation.mutate,
    isSettingDefaultTag: defaultTagMutation.isPending,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

/**
 * Every group the user can see, unpaged — for tree pickers and permission dialogs. Pages that
 * browse groups use {@link useRecordGroupSearch} instead.
 */
export function useRecordGroups() {
  const { currentWorkspaceId } = useWorkspace()
  const mutations = useRecordGroupMutations()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups'],
    queryFn: getRecordGroups,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  return {
    recordGroups: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    ...mutations,
  }
}

/** One group with its breadcrumb trail. */
export function useRecordGroup(
  recordGroupId: string | undefined,
  options: { keepPreviousData?: boolean } = {},
) {
  const { currentWorkspaceId } = useWorkspace()
  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', 'detail', recordGroupId],
    queryFn: () => getRecordGroup(recordGroupId!),
    enabled: !!currentWorkspaceId && !!recordGroupId,
    staleTime: 60 * 1000,
    retry: false,
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  })
}

/** Paged record group browser/search; keeps the previous page on screen while the next loads. */
export function useRecordGroupSearch(params: RecordGroupSearchParams) {
  const { currentWorkspaceId } = useWorkspace()
  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', 'search', params],
    queryFn: () => searchRecordGroups(params),
    enabled: !!currentWorkspaceId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

/** Overview counts over the visible groups (all, or everything below a parent). */
export function useRecordGroupSummary(parentId?: string) {
  const { currentWorkspaceId } = useWorkspace()
  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', 'summary', parentId ?? null],
    queryFn: () => getRecordGroupSummary(parentId),
    enabled: !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })
}

export function useRecordGroupTrash(pageNumber: number, pageSize: number) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash', pageNumber, pageSize],
    queryFn: () => getTrashedRecordGroups(pageNumber, pageSize),
    enabled: !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })

  const statsQuery = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash-stats'],
    queryFn: getRecordGroupTrashStats,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  const restoreMutation = useMutation({
    mutationFn: restoreRecordGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash-stats'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups'],
      })
    },
  })

  const permanentlyDeleteMutation = useMutation({
    mutationFn: permanentlyDeleteRecordGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash-stats'],
      })
    },
  })

  const cleanupMutation = useMutation({
    mutationFn: triggerRecordGroupTrashCleanup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash'],
      })
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups-trash-stats'],
      })
    },
  })

  return {
    trashedRecordGroups: query.data?.content ?? [],
    pagination: {
      totalElements: query.data?.totalElements ?? 0,
      totalPages: query.data?.totalPages ?? 0,
      currentPage: query.data?.number ?? 0,
      pageSize: query.data?.size ?? 10,
      isFirst: query.data?.first ?? true,
      isLast: query.data?.last ?? true,
      isEmpty: query.data?.empty ?? true,
    },
    isLoading: query.isLoading,
    error: query.error,
    stats: statsQuery.data,
    statsIsLoading: statsQuery.isLoading,
    restore: restoreMutation.mutate,
    permanentlyDelete: permanentlyDeleteMutation.mutate,
    cleanup: cleanupMutation.mutate,
    isRestoring: restoreMutation.isPending,
    isPermanentlyDeleting: permanentlyDeleteMutation.isPending,
    isCleaningUp: cleanupMutation.isPending,
    restoreError: restoreMutation.error,
    permanentlyDeleteError: permanentlyDeleteMutation.error,
    cleanupError: cleanupMutation.error,
  }}
